import {
  useGetSingleJobPostQuery,
  useTalentHuntSendEmailsMutation,
} from '@/app/features/recruitment/job-applicants/job-applicants.api';
import type { TalentHuntApplicantItem } from '@/app/features/recruitment/job-applicants/job-applicants.type';
import CustomDrawer from '@/components/CustomDrawer';
import SingleFileUploader from '@/components/SingleFileUploader';
import useAuth from '@/hooks/useAuth';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { Button, Flex, Form, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useParams } from 'react-router';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'align',
  'link',
];

type Props = {
  selectedApplicants?: TalentHuntApplicantItem[];
  type?: string;
  open: boolean;
  handleClose: (value?: boolean) => void;
};

export default function SendEmailModal({
  open,
  handleClose,
  selectedApplicants = [],
}: Props) {
  const { id } = useParams();
  const { profileInfo } = useAuth();
  const [form] = Form.useForm();
  const [rendered, setRendered] = useState(false);

  const { data: jobData } = useGetSingleJobPostQuery(id || '', { skip: !id });
  const [sendEmails, { isLoading }] = useTalentHuntSendEmailsMutation();

  const appLink = `https://hrxbd.org/job-portal/${id}`;
  const deadline = jobData?.data?.job_application_deadline
    ? dayjs(jobData.data.job_application_deadline).format('MMMM D, YYYY')
    : '--';
  const companyName = profileInfo?.organization?.name || '[Company Name]';

  // Build email options from selected applicants
  const emailOptions = selectedApplicants.map((a) => ({
    label: a.email,
    value: a.email,
  }));

  // Pre-fill fields whenever modal opens
  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        subject: 'You’re a Strong Match — Invitation to Apply',
        mail_body: `<p>Hello,</p>
<p>We came across your profile through our TalentHunt AI system, and you are one of the top matches for a role we are currently hiring for.</p>
<p><br></p>
<p>We would like to invite you to apply using the link below:</p>
<p><a href="${appLink}" rel="noopener noreferrer" target="_blank">${appLink}</a></p>
<p>Last day of application : ${deadline}</p>
<p><br></p>
<p>We look forward to learning more about you.</p>
<p><br></p>
<p>Best regards,</p>
<p>${companyName}</p>`,
      });

      // Fix for ReactQuill not rendering on first open
      const timer = setTimeout(() => {
        setRendered(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setRendered(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form]);

  const handleFinish = async (values: any) => {
    const payload = {
      job_post_id: id,
      mail_list: values.mail_list,
      subject: values.subject,
      mail_body: values.mail_body,
      application_link: values.application_link,
      attachment_url: values.attachment_url,
    };
    try {
      await sendEmails(payload).unwrap();
      toast('success', 'Emails sent successfully');
      form.resetFields();
      handleClose();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to send emails'));
    }
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={() => {
        handleClose();
        form.resetFields();
      }}
      title={'Email To Candidates'}
    >
      {open && (
        <Form
          form={form}
          size="large"
          layout="vertical"
          className="custom-form dark"
          onFinish={handleFinish}
        >
          <div className="space-y-6">
            <Form.Item
              label="Subject"
              name="subject"
              rules={[{ required: true, message: 'Please enter subject' }]}
            >
              <Input
                className="!rounded-lg !bg-theme-slate-900 !border-theme-gray-700 !text-white"
                placeholder="Looking For UI/UX Designer"
              />
            </Form.Item>

            <Form.Item
              label={'Mail Sent'}
              name="mail_list"
              rules={[
                {
                  required: true,
                  message: 'Please add at least one recipient',
                },
              ]}
            >
              <Select
                mode="multiple"
                className="custom-select-tags"
                placeholder="Select recipients"
                style={{ width: '100%' }}
                options={emailOptions}
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              label="Mail Body"
              name="mail_body"
              rules={[{ required: true, message: 'Please enter mail body' }]}
              getValueFromEvent={(value: string) => value}
            >
              {rendered ? (
                <ReactQuill
                  theme="snow"
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Enter mail body content here..."
                />
              ) : (
                <div className="h-[220px] bg-theme-slate-900 border border-theme-gray-700 rounded-lg animate-pulse" />
              )}
            </Form.Item>

            <Form.Item
              label="Application Link"
              name="application_link"
              initialValue={`https://hrxbd.org/job-portal/${id}`}
              rules={[
                { required: true, message: 'Please enter application link' },
              ]}
            >
              <Input
                disabled
                className="!rounded-lg !bg-theme-slate-900 !border-theme-gray-700 !text-white"
                placeholder="http://www.sample.org/head"
              />
            </Form.Item>

            <Form.Item name="attachment_url">
              <SingleFileUploader name="attachment_url" form={form} />
            </Form.Item>

            <Flex justify="end" className="mt-8">
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={isLoading}
                className="!bg-theme-primary !border-theme-primary !text-[#050505] !font-semibold !px-10 !rounded-lg"
              >
                Send
              </Button>
            </Flex>
          </div>
        </Form>
      )}
    </CustomDrawer>
  );
}
